import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { AppSidebar } from "./nav/AppSidebar";
import { MobileNav } from "./nav/MobileNav";
import { PlayNookLogo } from "./PlayNookLogo";
import { SiteFooter } from "./SiteFooter";
import { UserDropdown } from "./UserDropdown";

export function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const mainRef = useRef<HTMLElement>(null);
  // Compared against, rather than a "first render" flag, so StrictMode's
  // double-invoked effect doesn't steal focus on cold load.
  const previousPathname = useRef(pathname);

  useEffect(() => {
    if (previousPathname.current === pathname) return;
    previousPathname.current = pathname;
    // Put the virtual cursor at the top of the new page. Targeting <main>
    // rather than the heading means this works while the page is still a
    // skeleton, with no coordination with async data.
    mainRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);

  // Deliberately a <button>, not <a href="#main-content">: the app uses hash
  // history, which would read that as a route change to /main-content, and
  // ConfirmOnExit capture-intercepts same-origin anchor clicks document-wide.
  const skipToContent = () => {
    const main = mainRef.current;
    if (!main) return;
    main.focus();
    main.scrollIntoView({ block: "start", behavior: "auto" });
  };

  return (
    <div className="bg-background min-h-screen">
      <button
        type="button"
        onClick={skipToContent}
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:shadow-md focus-ring"
      >
        Skip to main content
      </button>
      <div className="relative isolate min-h-screen overflow-x-clip">
        <svg
          className="absolute inset-0 -z-10 h-full w-full stroke-foreground/10 mask-[radial-gradient(100%_100%_at_top_right,white,transparent)]"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="983e3e4c-de6d-4c3f-8d64-b9761d1534cc"
              width={200}
              height={200}
              x="50%"
              y={-1}
              patternUnits="userSpaceOnUse"
            >
              <path d="M.5 200V.5H200" fill="none" />
            </pattern>
          </defs>
          <svg
            x="50%"
            y={-1}
            className="overflow-visible fill-muted/20"
            aria-hidden="true"
          >
            <path
              d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
              strokeWidth={0}
            />
          </svg>
          <rect
            width="100%"
            height="100%"
            strokeWidth={0}
            fill="url(#983e3e4c-de6d-4c3f-8d64-b9761d1534cc)"
          />
        </svg>
        <div
          className="absolute left-[calc(50%-4rem)] top-10 -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] lg:left-48 lg:top-[calc(50%-30rem)] xl:left-[calc(50%-24rem)]"
          aria-hidden="true"
        >
          <div
            className="aspect-[1108/632] w-[69.25rem] bg-gradient-to-r from-[#80caff] to-[#4f46e5] opacity-20"
            style={{
              clipPath:
                "polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)",
            }}
          />
        </div>
        {/* 88rem = the rail and its gap (16) + the 68rem content column this had
            before the sidebar, so the card grid keeps its four columns. */}
        <div className="mx-auto max-w-352 px-6 pb-24 pt-6 sm:pb-40 lg:flex lg:gap-8 lg:px-8 lg:pt-8">
          <AppSidebar />
          <div className="mx-auto w-full min-w-0 flex-1 lg:mx-0 text-foreground">
            <header className="flex flex-row justify-between items-center gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <MobileNav />
                {/* min-w-0 on both: a flex item defaults to min-width:auto, so
                    without it the wordmark refuses to shrink and runs under the
                    account button, which carries shrink-0 from Button itself. */}
                <Link to="/" className="flex min-w-0 gap-2 items-center">
                  <span className="shrink-0">
                    <PlayNookLogo />
                  </span>
                  {/* Not a heading: it repeats on every route and would make
                      heading navigation start with noise ahead of the page's h1. */}
                  <span className="min-w-0 truncate text-xl font-bold leading-7 sm:text-3xl sm:tracking-tight">
                    PlayNook Questly
                  </span>
                </Link>
              </div>
              <UserDropdown />
            </header>
            <main
              id="main-content"
              tabIndex={-1}
              ref={mainRef}
              className="outline-none"
            >
              <Outlet />
            </main>
            {/* After <main> so the skip control stays the first tab stop and
                the footer's links land after the page's own. */}
            <SiteFooter />
          </div>
        </div>
      </div>
    </div>
  );
}
